// Pager Report Page for AAR Guest Management System
// Lists all missing/unreturned pagers, inventory management, and recovery tracking
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getCurrentUser, getUserRole } from '../firebase/authService';
import { canAccessReports } from '../utils/roles';
import { formatDate } from '../utils/dateUtils';
import {
  fetchParticipants,
  fetchPagerSettings,
  updatePagerSettings,
  fetchMissingPagers,
  markPagerRecovered,
  markPagerReturnedFromCheckIn,
  fetchPagerRecoveryLog
} from '../firebase/firestoreService';
import placeholderAvatar from '../assets/placeholder-avatar.svg';
import '../styles/PagerReport.css';
import { readOnlyProps } from '../utils/viewAs';

const PagerReport = () => {
  const ro = readOnlyProps(); // disable write controls while viewing as someone
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [timeframe, setTimeframe] = useState('month');
  const [reportData, setReportData] = useState(null);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('missing'); // 'missing', 'recovery'
  const navigate = useNavigate();
  const user = getCurrentUser();

  // Inventory state
  const [totalPagers, setTotalPagers] = useState(0);
  const [editingTotal, setEditingTotal] = useState(false);
  const [newTotalInput, setNewTotalInput] = useState('');
  const [missingPagersList, setMissingPagersList] = useState([]);
  const [recoveryLog, setRecoveryLog] = useState([]);
  const [allUnreturnedPagerNumbers, setAllUnreturnedPagerNumbers] = useState(new Set());

  // Recovery modal
  const [showRecoveryModal, setShowRecoveryModal] = useState(false);
  const [recoveryPagerNum, setRecoveryPagerNum] = useState('');
  const [recoveryReturnedBy, setRecoveryReturnedBy] = useState('');
  const [recoveryNotes, setRecoveryNotes] = useState('');
  const [recoverySubmitting, setRecoverySubmitting] = useState(false);
  const [recoveryCheckInId, setRecoveryCheckInId] = useState(null);
  const [recoveryGuestName, setRecoveryGuestName] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    getUserRole(user.uid).then(role => {
      if (!canAccessReports(role)) {
        navigate('/dashboard');
      } else {
        setLoading(false);
      }
    });
  }, [user, navigate]);

  const loadInventoryData = useCallback(async () => {
    try {
      const [settings, missing, log] = await Promise.all([
        fetchPagerSettings(),
        fetchMissingPagers(),
        fetchPagerRecoveryLog()
      ]);
      setTotalPagers(settings.totalPagers || 0);
      setMissingPagersList(missing);
      setRecoveryLog(log);

      // Also fetch ALL unreturned pagers (all-time) for the summary counts
      const checkInsRef = collection(db, 'checkIns');
      const allQ = query(
        checkInsRef,
        where('status', '==', 'checked_out')
      );
      const allSnapshot = await getDocs(allQ);
      const unreturned = new Set();
      allSnapshot.forEach((docSnap) => {
        const d = docSnap.data();
        if (d.pagerNumber && d.pagerRetrieved !== true && d.guestDeleted !== true) {
          unreturned.add(String(d.pagerNumber));
        }
      });
      setAllUnreturnedPagerNumbers(unreturned);
    } catch (err) {
      console.error('Error loading inventory data:', err);
    }
  }, []);

  useEffect(() => {
    if (!loading) {
      loadInventoryData();
    }
  }, [loading, loadInventoryData]);

  useEffect(() => {
    if (!loading && !generating) {
      generateReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, timeframe]);

  const getTimeframeRange = (tf) => {
    const now = new Date();
    const start = new Date();

    switch (tf) {
      case 'week':
        start.setDate(now.getDate() - 7);
        break;
      case 'month':
        start.setMonth(now.getMonth() - 1);
        break;
      case 'quarter':
        start.setMonth(now.getMonth() - 3);
        break;
      case 'semi-annual':
        start.setMonth(now.getMonth() - 6);
        break;
      case 'annual':
        start.setFullYear(now.getFullYear() - 1);
        break;
      case 'all':
        start.setFullYear(2020, 0, 1);
        break;
      default:
        start.setMonth(now.getMonth() - 1);
    }

    return { start, end: now };
  };

  const formatServiceType = (serviceType) => {
    if (serviceType === 'tuesday_prayer') return 'TPM';
    return serviceType || 'N/A';
  };

  const generateReport = async () => {
    try {
      setGenerating(true);
      setError('');

      const { start, end } = getTimeframeRange(timeframe);

      // Fetch participants for guest images
      const participants = await fetchParticipants('1269');

      const checkInsRef = collection(db, 'checkIns');
      // Query by status only (no checkInTime range). A checkInTime range query
      // silently drops any record whose checkInTime field is missing or is not a
      // Firestore Timestamp — which made those unreturned pagers vanish from
      // EVERY timeframe, including "All Time", while still being counted by the
      // KPI. We filter by timeframe in-memory below instead.
      const q = query(
        checkInsRef,
        where('status', '==', 'checked_out')
      );

      const querySnapshot = await getDocs(q);
      const missingPagers = [];

      querySnapshot.forEach((docSnap) => {
        const checkIn = { id: docSnap.id, ...docSnap.data() };

        // Skip deleted guests
        if (checkIn.guestDeleted === true) return;

        // Only include check-ins that have a pager number
        if (!checkIn.pagerNumber) return;

        // Only include checked-out guests where pager was NOT retrieved
        if (checkIn.pagerRetrieved === true) return;

        // Timeframe filter, applied in-memory. Records with a missing/invalid
        // checkInTime are ALWAYS kept so a stuck pager can never disappear from
        // the list (that is exactly the bug this report is meant to surface).
        if (timeframe !== 'all') {
          const inDate = checkIn.checkInTime?.toDate?.();
          if (inDate && (inDate < start || inDate > end)) return;
        }

        // Find guest info for image
        const guestInfo = participants.find(g =>
          g.firstName === checkIn.guestFirstName &&
          g.lastName === checkIn.guestLastName
        );

        missingPagers.push({
          id: checkIn.id,
          pagerNumber: String(checkIn.pagerNumber),
          guestName: `${checkIn.guestFirstName} ${checkIn.guestLastName}`,
          firstName: checkIn.guestFirstName,
          lastName: checkIn.guestLastName,
          image: guestInfo?.image || null,
          serviceType: checkIn.serviceType,
          checkInTime: checkIn.checkInTime,
          checkOutTime: checkIn.checkOutTime,
          checkOutByName: checkIn.checkOutByName || 'Unknown',
          checkInNotes: checkIn.checkInNotes || '',
          checkOutNotes: checkIn.checkOutNotes || ''
        });
      });

      // Sort by check-out date descending (most recent first)
      missingPagers.sort((a, b) => {
        const dateA = a.checkOutTime?.toDate?.() || new Date(0);
        const dateB = b.checkOutTime?.toDate?.() || new Date(0);
        return dateB - dateA;
      });

      setReportData({
        timeframe,
        startDate: start,
        endDate: end,
        totalMissing: missingPagers.length,
        pagers: missingPagers
      });
    } catch (err) {
      console.error('Error generating pager report:', err);
      setError('Failed to generate report. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleUpdateTotalPagers = async () => {
    const newTotal = parseInt(newTotalInput, 10);
    if (isNaN(newTotal) || newTotal < 0) {
      setError('Please enter a valid number.');
      return;
    }
    try {
      await updatePagerSettings(newTotal);
      setEditingTotal(false);
      setNewTotalInput('');
      await loadInventoryData();
    } catch (err) {
      setError('Failed to update total pagers.');
    }
  };

  const handleRecoverPager = async () => {
    if (!recoveryPagerNum.trim()) {
      setError('Please enter a pager number.');
      return;
    }
    if (!recoveryReturnedBy.trim()) {
      setError('Please enter the name of the person who returned the pager.');
      return;
    }

    try {
      setRecoverySubmitting(true);
      if (recoveryCheckInId) {
        // Marking returned from the unreturned pagers list
        await markPagerReturnedFromCheckIn(
          recoveryCheckInId,
          recoveryPagerNum.trim(),
          user.uid,
          user.displayName || user.email,
          recoveryReturnedBy.trim(),
          recoveryNotes
        );
      } else {
        // Standalone recovery (from inventory)
        await markPagerRecovered(
          recoveryPagerNum.trim(),
          user.uid,
          user.displayName || user.email,
          recoveryReturnedBy.trim(),
          recoveryNotes
        );
      }
      setShowRecoveryModal(false);
      setRecoveryPagerNum('');
      setRecoveryReturnedBy('');
      setRecoveryNotes('');
      setRecoveryCheckInId(null);
      setRecoveryGuestName('');
      await loadInventoryData();
      await generateReport();
    } catch (err) {
      setError('Failed to record pager recovery.');
    } finally {
      setRecoverySubmitting(false);
    }
  };

  // Open recovery modal for an unreturned pager from the check-in list
  const openReturnForPager = (pager) => {
    setRecoveryPagerNum(pager.pagerNumber);
    setRecoveryCheckInId(pager.id);
    setRecoveryGuestName(pager.guestName);
    setRecoveryReturnedBy('');
    setRecoveryNotes('');
    setShowRecoveryModal(true);
    setError('');
  };

  // Open recovery modal for a pager flagged missing in inventory (e.g. auto-
  // marked at checkout). No live check-in to update, so recover it standalone —
  // this deletes the pagerInventory record and frees the pager for check-in.
  const openRecoverInventoryPager = (p) => {
    setRecoveryPagerNum(String(p.pagerNumber));
    setRecoveryCheckInId(null);
    setRecoveryGuestName(p.guestName || '');
    setRecoveryReturnedBy('');
    setRecoveryNotes('');
    setShowRecoveryModal(true);
    setError('');
  };

  const exportToCSV = () => {
    if (!reportData || reportData.pagers.length === 0) return;

    const headers = ['Pager #', 'Guest Name', 'Service', 'Check-Out Date', 'Check-Out Time', 'Checked Out By', 'Notes'];
    const rows = reportData.pagers.map(p => [
      `"${p.pagerNumber}"`,
      `"${p.guestName}"`,
      `"${formatServiceType(p.serviceType)}"`,
      `"${formatDate(p.checkOutTime, 'MM/dd/yyyy')}"`,
      `"${formatDate(p.checkOutTime, 'h:mm a')}"`,
      `"${p.checkOutByName}"`,
      `"${p.checkOutNotes || p.checkInNotes || ''}"`
    ]);

    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `missing-pagers-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportToPDF = () => {
    if (!reportData || reportData.pagers.length === 0) return;

    const printWindow = window.open('', '_blank');
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Missing Pagers Report</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; color: #333; }
          h1 { text-align: center; color: #1a5f7a; margin-bottom: 4px; }
          .subtitle { text-align: center; color: #666; margin-bottom: 20px; font-size: 14px; }
          .summary { display: flex; justify-content: center; gap: 30px; margin-bottom: 20px; }
          .summary-item { text-align: center; }
          .summary-label { font-size: 12px; color: #666; }
          .summary-value { font-size: 24px; font-weight: 700; color: #ef4444; }
          .summary-value.green { color: #16a34a; }
          table { width: 100%; border-collapse: collapse; font-size: 13px; }
          th { background: #1a5f7a; color: white; padding: 10px 8px; text-align: left; }
          td { padding: 8px; border-bottom: 1px solid #e5e7eb; }
          tr:nth-child(even) { background: #f9fafb; }
          .pager-num { font-weight: 700; color: #ef4444; font-size: 15px; }
          .notes { font-size: 11px; color: #666; font-style: italic; }
          @media print { body { margin: 10px; } }
        </style>
      </head>
      <body>
        <h1>Missing Pagers Report</h1>
        <div class="subtitle">
          ${formatDate(reportData.startDate, 'MMM d, yyyy')} - ${formatDate(reportData.endDate, 'MMM d, yyyy')}
        </div>
        <div class="summary">
          <div class="summary-item">
            <div class="summary-value">${totalPagers}</div>
            <div class="summary-label">Total Pagers</div>
          </div>
          <div class="summary-item">
            <div class="summary-value green">${totalPagers - missingPagersList.length}</div>
            <div class="summary-label">Available</div>
          </div>
          <div class="summary-item">
            <div class="summary-value">${missingPagersList.length}</div>
            <div class="summary-label">Missing (Inventory)</div>
          </div>
          <div class="summary-item">
            <div class="summary-value">${reportData.totalMissing}</div>
            <div class="summary-label">Unreturned (Timeframe)</div>
          </div>
        </div>
        <table>
          <thead>
            <tr>
              <th>Pager #</th>
              <th>Guest Name</th>
              <th>Service</th>
              <th>Check-Out Date & Time</th>
              <th>Checked Out By</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            ${reportData.pagers.map(p => `
              <tr>
                <td class="pager-num">${p.pagerNumber}</td>
                <td>${p.guestName}</td>
                <td>${formatServiceType(p.serviceType)}</td>
                <td>${formatDate(p.checkOutTime, 'MM/dd/yyyy h:mm a')}</td>
                <td>${p.checkOutByName}</td>
                <td class="notes">${p.checkOutNotes || p.checkInNotes || ''}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  const filteredPagers = reportData?.pagers?.filter(p => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      p.pagerNumber.toLowerCase().includes(term) ||
      p.guestName.toLowerCase().includes(term) ||
      p.checkOutByName.toLowerCase().includes(term)
    );
  }) || [];

  if (loading) {
    return (
      <div className="loading"><div className="spinner"></div><p>Loading...</p></div>
    );
  }

  // Combine missing pagers from both sources: pagerInventory records + all unreturned from check-ins
  const allMissingPagerNumbers = new Set(allUnreturnedPagerNumbers);
  missingPagersList.forEach(p => allMissingPagerNumbers.add(String(p.pagerNumber)));
  const totalMissingCount = allMissingPagerNumbers.size;
  const availablePagers = totalPagers - totalMissingCount;

  // Helper to get initials from a name
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    return parts.map(p => p[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div className="pager-report-container">
      <header className="pager-report-header">
        <button className="btn-back" onClick={() => navigate('/admin')}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
          Admin Panel
        </button>
        <h1 className="pager-report-title">Pager Report</h1>
      </header>

      <div className="pager-report-content">
        {/* Minimalist KPI Cards */}
        <section className="report-summary">
          <div className="summary-card">
            <div className="summary-label">
              Total Inventory
              {!editingTotal && (
                <button
                  className="btn-edit-total"
                  disabled={ro.disabled}
                  title={ro.title || 'Edit Inventory'}
                  onClick={() => {
                    setEditingTotal(true);
                    setNewTotalInput(String(totalPagers));
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg>
                </button>
              )}
            </div>
            {!editingTotal ? (
              <div className="summary-number inventory-total">{totalPagers}</div>
            ) : (
              <div className="edit-total-inline">
                <input
                  type="number"
                  min="0"
                  value={newTotalInput}
                  onChange={(e) => setNewTotalInput(e.target.value)}
                  className="edit-total-input"
                  autoFocus
                />
                <button className="btn-save-total" onClick={handleUpdateTotalPagers} disabled={ro.disabled} title={ro.title}>Save</button>
                <button className="btn-cancel-total" onClick={() => setEditingTotal(false)}>Cancel</button>
              </div>
            )}
          </div>
          <div className="summary-card">
            <div className="summary-label">Available</div>
            <div className="summary-number available-number">{availablePagers >= 0 ? availablePagers : 0}</div>
          </div>
          <div
            className={`summary-card${totalMissingCount > 0 ? ' summary-card-clickable' : ''}`}
            onClick={() => {
              if (totalMissingCount === 0) return;
              setActiveTab('missing');
              setTimeframe('all');
            }}
            title={totalMissingCount > 0 ? 'Show these pagers in the Unreturned list (All Time)' : undefined}
          >
            <div className="summary-label">Not in Inventory</div>
            <div className="summary-number" style={{ color: totalMissingCount > 0 ? 'var(--danger)' : 'var(--text-primary)' }}>{totalMissingCount}</div>
            {totalMissingCount > 0 && (
              <div className="summary-subnote">
                Pager {[...allMissingPagerNumbers].sort((a, b) => Number(a) - Number(b)).join(', ')}
              </div>
            )}
          </div>
        </section>

        {/* Underline Tabs */}
        <nav className="pager-tabs">
          <button
            className={`pager-tab ${activeTab === 'missing' ? 'active' : ''}`}
            onClick={() => setActiveTab('missing')}
          >
            Unreturned Pagers
            {reportData && reportData.totalMissing > 0 && (
              <span className="tab-badge">{reportData.totalMissing}</span>
            )}
          </button>
          <button
            className={`pager-tab ${activeTab === 'recovery' ? 'active' : ''}`}
            onClick={() => setActiveTab('recovery')}
          >
            Recovery Log
          </button>
        </nav>

        {error && <div className="error-message">{error}</div>}

        {/* ===== UNRETURNED PAGERS TAB ===== */}
        {activeTab === 'missing' && (
          <>
            {/* Pagers flagged missing in inventory (auto-marked at checkout, or
                orphaned when a check-in was later deleted). These are NOT tied
                to a timeframe and would otherwise be counted but never shown. */}
            {missingPagersList.length > 0 && (
              <div className="report-table-container inventory-missing-section">
                <div className="inventory-missing-heading">
                  Missing from Inventory
                  <span className="inventory-missing-sub">
                    Not in the check-in pool. Mark returned to make them available again.
                  </span>
                </div>
                <table className="pager-table unreturned-table">
                  <thead>
                    <tr>
                      <th>Pager #</th>
                      <th>Last Guest</th>
                      <th>Flagged Missing</th>
                      <th>By</th>
                      <th>Notes</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {missingPagersList.map((p) => (
                      <tr key={p.id}>
                        <td><span className="pager-number">{p.pagerNumber}</span></td>
                        <td>{p.guestName || '—'}</td>
                        <td>
                          {p.markedMissingAt ? (
                            <>
                              <div className="checkout-date">{formatDate(p.markedMissingAt, 'MMM d, yyyy')}</div>
                              <div className="checkout-time">{formatDate(p.markedMissingAt, 'h:mm a')}</div>
                            </>
                          ) : '—'}
                        </td>
                        <td>{p.markedMissingByName || 'Unknown'}</td>
                        <td><span className="notes-text">{p.notes || '—'}</span></td>
                        <td>
                          <button className="btn-recover" onClick={() => openRecoverInventoryPager(p)}>
                            Mark Returned
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Toolbar */}
            <div className="report-controls">
              <div className="control-group">
                <select
                  value={timeframe}
                  onChange={(e) => setTimeframe(e.target.value)}
                  className="control-select"
                >
                  <option value="week">Past Week</option>
                  <option value="month">Past Month</option>
                  <option value="quarter">Past 3 Months</option>
                  <option value="semi-annual">Past 6 Months</option>
                  <option value="annual">Past Year</option>
                  <option value="all">All Time</option>
                </select>
              </div>

              <div className="search-wrapper">
                <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
                <input
                  type="text"
                  placeholder="Search by pager #, guest, or admin..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>

              {reportData && reportData.pagers.length > 0 && (
                <div className="export-buttons">
                  <button className="btn-export" onClick={exportToCSV}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>
                    Export CSV
                  </button>
                  <button className="btn-export" onClick={exportToPDF}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>
                    Export PDF
                  </button>
                </div>
              )}
            </div>

            {/* Results Table */}
            {reportData && (
              <div className="report-table-container">
                {filteredPagers.length === 0 ? (
                  <div className="empty-state">
                    {reportData.totalMissing === 0
                      ? 'No unreturned pagers found for this timeframe.'
                      : 'No results matching your search.'}
                  </div>
                ) : (
                  <table className="pager-table unreturned-table">
                    <thead>
                      <tr>
                        <th>Pager #</th>
                        <th>Guest</th>
                        <th>Service</th>
                        <th>Checked Out</th>
                        <th>Admin</th>
                        <th>Notes</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPagers.map((pager) => (
                        <tr key={pager.id}>
                          <td>
                            <span className="pager-number">{pager.pagerNumber}</span>
                          </td>
                          <td>
                            <div className="guest-cell">
                              {pager.image ? (
                                <div className="guest-avatar-small">
                                  <img src={pager.image} alt={pager.guestName} />
                                </div>
                              ) : (
                                <div className="guest-avatar-initials">
                                  {getInitials(pager.guestName)}
                                </div>
                              )}
                              <span className="guest-name">{pager.guestName}</span>
                            </div>
                          </td>
                          <td>
                            <span className="service-badge">{formatServiceType(pager.serviceType)}</span>
                          </td>
                          <td>
                            <div className="checkout-date">{formatDate(pager.checkOutTime, 'MMM d, yyyy')}</div>
                            <div className="checkout-time">{formatDate(pager.checkOutTime, 'h:mm a')}</div>
                          </td>
                          <td>{pager.checkOutByName}</td>
                          <td>
                            <span className="notes-text">
                              {pager.checkOutNotes || pager.checkInNotes || '\u2014'}
                            </span>
                          </td>
                          <td>
                            <button
                              className="btn-recover"
                              onClick={() => openReturnForPager(pager)}
                            >
                              Mark Returned
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* Mobile Card View */}
            {reportData && filteredPagers.length > 0 && (
              <div className="pager-cards-mobile">
                {filteredPagers.map((pager) => (
                  <div key={pager.id} className="pager-card">
                    <div className="pager-card-header">
                      <span className="pager-number-large">Pager {pager.pagerNumber}</span>
                      <span className="service-badge">{formatServiceType(pager.serviceType)}</span>
                    </div>
                    <div className="pager-card-body">
                      <div className="pager-card-guest">
                        {pager.image ? (
                          <div className="guest-avatar-small">
                            <img src={pager.image} alt={pager.guestName} />
                          </div>
                        ) : (
                          <div className="guest-avatar-initials">
                            {getInitials(pager.guestName)}
                          </div>
                        )}
                        <span className="guest-name">{pager.guestName}</span>
                      </div>
                      <div className="pager-card-details">
                        <div className="detail-row">
                          <span className="detail-label">Checked Out:</span>
                          <span>{formatDate(pager.checkOutTime, 'MMM d, yyyy')} at {formatDate(pager.checkOutTime, 'h:mm a')}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Admin:</span>
                          <span>{pager.checkOutByName}</span>
                        </div>
                        {(pager.checkOutNotes || pager.checkInNotes) && (
                          <div className="detail-row">
                            <span className="detail-label">Notes:</span>
                            <span className="notes-text">{pager.checkOutNotes || pager.checkInNotes}</span>
                          </div>
                        )}
                      </div>
                      <div className="pager-card-actions">
                        <button
                          className="btn-recover"
                          onClick={() => openReturnForPager(pager)}
                        >
                          Mark Returned
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ===== RECOVERY LOG TAB ===== */}
        {activeTab === 'recovery' && (
          <>
            <div className="report-table-container">
              {recoveryLog.length === 0 ? (
                <div className="empty-state">
                  No recovery activity recorded yet.
                </div>
              ) : (
                <table className="pager-table">
                  <thead>
                    <tr>
                      <th>Pager #</th>
                      <th>Action</th>
                      <th>Date</th>
                      <th>Performed By</th>
                      <th>Returned By</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recoveryLog.map((entry) => (
                      <tr key={entry.id}>
                        <td>
                          <span className="pager-number">{entry.pagerNumber}</span>
                        </td>
                        <td>
                          <span className={`status-badge ${entry.action === 'recovered' ? 'recovered-badge' : 'missing-badge'}`}>
                            {entry.action === 'recovered' ? 'Recovered' : 'Marked Missing'}
                          </span>
                        </td>
                        <td>
                          {entry.timestamp ? (
                            <>
                              <div className="checkout-date">{formatDate(entry.timestamp, 'MMM d, yyyy')}</div>
                              <div className="checkout-time">{formatDate(entry.timestamp, 'h:mm a')}</div>
                            </>
                          ) : 'N/A'}
                        </td>
                        <td>{entry.performedByName || 'Unknown'}</td>
                        <td>{entry.returnedByName || '\u2014'}</td>
                        <td>
                          <span className="notes-text">{entry.notes || '\u2014'}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Mobile card view for recovery log */}
            {recoveryLog.length > 0 && (
              <div className="pager-cards-mobile">
                {recoveryLog.map((entry) => (
                  <div key={entry.id} className="pager-card">
                    <div className="pager-card-header">
                      <span className="pager-number-large">{entry.pagerNumber}</span>
                      <span className={`status-badge ${entry.action === 'recovered' ? 'recovered-badge' : 'missing-badge'}`}>
                        {entry.action === 'recovered' ? 'Recovered' : 'Marked Missing'}
                      </span>
                    </div>
                    <div className="pager-card-body">
                      <div className="pager-card-details">
                        <div className="detail-row">
                          <span className="detail-label">Date:</span>
                          <span>
                            {entry.timestamp
                              ? formatDate(entry.timestamp, 'MM/dd/yyyy h:mm a')
                              : 'N/A'}
                          </span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">By:</span>
                          <span>{entry.performedByName || 'Unknown'}</span>
                        </div>
                        {entry.returnedByName && (
                          <div className="detail-row">
                            <span className="detail-label">Returned By:</span>
                            <span>{entry.returnedByName}</span>
                          </div>
                        )}
                        {entry.notes && (
                          <div className="detail-row">
                            <span className="detail-label">Notes:</span>
                            <span className="notes-text">{entry.notes}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* ===== RECOVERY MODAL ===== */}
      {showRecoveryModal && (
        <div className="modal-overlay" onClick={() => setShowRecoveryModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">Mark Pager as Returned</h2>
            <p className="modal-description">
              Record that pager <strong>#{recoveryPagerNum}</strong> has been returned.
              {recoveryGuestName && <> Last assigned to <strong>{recoveryGuestName}</strong>.</>}
              {' '}This will update the pager status and make it available for future assignments.
            </p>
            <div className="modal-form">
              <div className="form-group">
                <label>Pager Number</label>
                <input
                  type="text"
                  value={recoveryPagerNum}
                  className="modal-input"
                  disabled
                />
              </div>
              <div className="form-group">
                <label>Returned By (Required)</label>
                <input
                  type="text"
                  value={recoveryReturnedBy}
                  onChange={(e) => setRecoveryReturnedBy(e.target.value)}
                  placeholder="Name of person who returned the pager"
                  className="modal-input"
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>Notes (Optional)</label>
                <textarea
                  value={recoveryNotes}
                  onChange={(e) => setRecoveryNotes(e.target.value)}
                  placeholder="Any details about the recovery..."
                  className="modal-textarea"
                  rows="3"
                />
              </div>
            </div>
            <div className="modal-actions">
              <button
                className="btn btn-cancel"
                onClick={() => {
                  setShowRecoveryModal(false);
                  setRecoveryCheckInId(null);
                  setRecoveryGuestName('');
                }}
                disabled={recoverySubmitting}
              >
                Cancel
              </button>
              <button
                className="btn btn-success"
                onClick={handleRecoverPager}
                disabled={recoverySubmitting || ro.disabled}
                title={ro.title}
              >
                {recoverySubmitting ? 'Saving...' : 'Mark Returned'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PagerReport;
